import { ApiError, isRateLimitedError } from "./core";

/**
 * One rendering of a failed request, chosen by the server's STRUCTURED verdict
 * (status + `code`), never by the message text.
 *
 * The three cases a page has to tell apart are "come back later", "you need
 * access", and "we broke" — they lead to different actions (retry, sign in,
 * retry later), and collapsing them is how a rate-limited visitor used to get
 * bounced to a sign-in page that could not help.
 */
export function requestErrorCopy(
	error: unknown,
	{ subject = "This page" }: { subject?: string } = {}
): {
	title: string;
	body: string;
	/** A retry of the same request can plausibly succeed unchanged. */
	retryable: boolean;
} {
	if (isRateLimitedError(error)) {
		// The server's own message states the wait and who gets a higher limit;
		// rephrasing it here would be a second, drifting copy of the policy.
		return {
			title: "Too many requests",
			body: error.message,
			retryable: true,
		};
	}

	const status = error instanceof ApiError ? error.status : null;
	if (status === 401 || status === 403) {
		return {
			title: "Sign in to see this",
			body: "This workspace is public, but this part of it is for members. Sign in and join to continue.",
			retryable: false,
		};
	}

	if (status === 404) {
		return {
			title: "Not found",
			body: "It may have been renamed or removed.",
			retryable: false,
		};
	}

	return {
		title: `${subject} couldn’t be loaded`,
		body: error instanceof Error && error.message ? error.message : "Please try again in a moment.",
		// 5xx and transport failures are the only remaining class: the request
		// itself was fine, so retrying is the right affordance.
		retryable: true,
	};
}
